import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchInvitation,
  InvitationApiError
} from "@/data/invitations";
import { themePalette, themeToCssVars } from "@/lib/theme";
import InvitationCard from "@/components/InvitationCard";
import type { PublicInvitation } from "@/lib/types";

/**
 * Página de invitación pública (ruta `/i/:slug`).
 *
 * Carga los datos desde la API real (`GET /api/invitations/:slug`) vía el
 * gateway `fetchInvitation`. Gestiona cuatro estados:
 *
 *  - `loading`   → spinner sobre el fondo ambiental.
 *  - `notFound`  → 404 inline (no redirige, para no romper el "atrás").
 *  - `error`     → mensaje + botón de reintentar (fallos de red/servidor).
 *  - `success`   → renderiza la invitación con su tema aplicado.
 *
 * El tema se aplica como variables CSS inline en el contenedor, que las
 * utilidades de Tailwind (`text-accent`, `theme-bg`...) consumen.
 */
type Status = "loading" | "notFound" | "error" | "success";

export default function InvitationView() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [inv, setInv] = useState<PublicInvitation | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    fetchInvitation(slug)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setInv(data);
          setStatus("success");
        } else {
          setInv(null);
          setStatus("notFound");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof InvitationApiError
            ? err.message
            : "Something went wrong loading this invitation.";
        setErrorMsg(msg);
        setInv(null);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO social: título dinámico según el estado.
  useEffect(() => {
    document.title =
      status === "success" && inv
        ? `${inv.title} · Invitation`
        : "Invitation";
  }, [status, inv]);

  useEffect(() => {
    const isRomantic = status === "success" && inv?.theme === "romantic";
    const root = document.documentElement;
    const body = document.body;
    let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousThemeColor = themeMeta?.getAttribute("content") ?? null;

    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      document.head.appendChild(themeMeta);
    }

    root.classList.toggle("romantic-viewport-bg", isRomantic);
    body.classList.toggle("romantic-viewport-bg", isRomantic);
    if (isRomantic) themeMeta.setAttribute("content", "#f9dfe5");

    return () => {
      root.classList.remove("romantic-viewport-bg");
      body.classList.remove("romantic-viewport-bg");
      if (previousThemeColor) {
        themeMeta?.setAttribute("content", previousThemeColor);
      }
    };
  }, [status, inv?.theme]);

  if (status === "loading") {
    return (
      <Shell>
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-[rgb(var(--accent-strong))]"
          aria-label="Loading invitation"
        />
      </Shell>
    );
  }

  if (status === "notFound") {
    return (
      <Shell>
        <StatusBlock emoji="🔍" title="Invitation not found">
          This invitation may have expired or the link is incorrect.
          <RetryOrHome />
        </StatusBlock>
      </Shell>
    );
  }

  if (status === "error") {
    return (
      <Shell>
        <StatusBlock emoji="📡" title="Couldn’t load it">
          {errorMsg || "Something went wrong."}
          <button
            type="button"
            onClick={() => {
              // Forzar recarga del effect cambiando la key del contenedor no
              // es necesario: react-router re-ejecuta el effect al cambiar el
              // slug; para reintentar sin cambiar la URL, recargamos.
              window.location.reload();
            }}
            className="btn-primary mt-6 rounded-pill px-6 py-3 font-semibold"
          >
            Try again
          </button>
        </StatusBlock>
      </Shell>
    );
  }

  if (!inv) return null; // salvaguarda de tipos; el status controla el flujo.

  const palette = themePalette[inv.theme];
  const vars = themeToCssVars(palette) as React.CSSProperties;
  const isRomantic = inv.theme === "romantic";

  return (
    <main
      className={`theme-bg flex min-h-svh w-full flex-col items-center justify-center px-5 sm:px-6 lg:px-10 ${
        isRomantic
          ? "romantic-page py-5 sm:py-7 lg:py-8"
          : "py-10"
      }`}
      style={vars}
    >
      <InvitationCard inv={inv} />

      <footer
        className={`text-center text-xs ${
          isRomantic
            ? "mt-4 text-[13px] font-semibold text-[rgb(var(--subtext))] opacity-60"
            : "mt-8 opacity-50"
        }`}
      >
        Made with{" "}
        <Link
          to="/"
          className={isRomantic ? "underline decoration-current/50 underline-offset-4" : ""}
        >
          Invitely
        </Link>
      </footer>
    </main>
  );
}

/**
 * Contenedor base para los estados no-success. Reusa el fondo ambiental con
 * las variables globales (no las del tema, que aún no conocemos).
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="theme-bg flex min-h-svh w-full items-center justify-center px-6 py-16">
      {children}
    </main>
  );
}

/** Bloque de estado (loading/notFound/error) centrado y temático. */
function StatusBlock({
  emoji,
  title,
  children
}: {
  emoji: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="animate-scale-in flex max-w-sm flex-col items-center text-center">
      <div className="mb-4 text-6xl" aria-hidden>
        {emoji}
      </div>
      <h1 className="font-display text-3xl font-bold">{title}</h1>
      <div className="mt-3 flex flex-col items-center gap-1 text-base opacity-75">
        {children}
      </div>
    </div>
  );
}

/** Link de vuelta al inicio, reutilizado en los estados de fallo. */
function RetryOrHome() {
  return (
    <Link
      to="/"
      className="btn-primary mt-6 rounded-pill px-6 py-3 font-semibold"
    >
      Back home
    </Link>
  );
}
